// src/pages/SignCommodityContractPage.tsx — public commodity signing page.
// Same state machine as SignContractPage; commodity has no sign prereqs, so
// there are no waiting states.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios, { AxiosError } from "axios";
import { CommoditySignTokenModal } from "../components/addServices/CommoditySignTokenModal";
import {
  fetchCommodityContractByToken,
  signCommodityContractByToken,
  CommoditySignTokenData,
  CommoditySignPayload,
} from "../api/commoditySigning";
import {
  ContractErrorBody,
  resolveContractErrorCopy,
} from "../utils/contractErrorCodes";

type PageState =
  | "loading"
  | "ready"
  | "signing_success"
  | "error_expired"
  | "error_signed"
  | "error_invalid";

export default function SignCommodityContractPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation("addservice");
  const [state, setState] = useState<PageState>("loading");
  const [data, setData] = useState<CommoditySignTokenData | null>(null);

  useEffect(() => {
    if (!token) {
      setState("error_invalid");
      return;
    }

    fetchCommodityContractByToken(token)
      .then((res) => {
        setData(res);
        setState("ready");
      })
      .catch((err: AxiosError) => {
        if (err.response?.status === 410) {
          const message = (err.response?.data as { message?: string })?.message ?? "";
          setState(message.toLowerCase().includes("signed") ? "error_signed" : "error_expired");
        } else {
          setState("error_invalid");
        }
      });
  }, [token]);

  const handleSign = async (payload: CommoditySignPayload) => {
    if (!token) return;
    try {
      await signCommodityContractByToken(token, payload);
      setState("signing_success");
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 410) {
          const message = (err.response.data as { message?: string })?.message ?? "";
          setState(message.toLowerCase().includes("signed") ? "error_signed" : "error_expired");
          throw new Error(""); // terminal state rendered by the page; no inline message
        }
        // 409 carries a CustomerSwitchError, whose envelope is `{ error, code }` — NOT
        // the `{ message }` / `{ errors }` shape the rest of this route uses. Read it
        // through the shared map so the signatory is told WHY (e.g. the pinned crossover
        // date has already passed) instead of "something went wrong". Without this
        // branch the lines below find no `message` and no `errors`, fall straight to the
        // generic string, and the only actionable part of the response is discarded.
        if (err.response?.status === 409) {
          const body = err.response.data as ContractErrorBody | undefined;
          const { message, detail } = resolveContractErrorCopy(
            body,
            t,
            t("signingPage.submitError")
          );
          throw new Error(detail ? `${message} ${detail}` : message);
        }
        const data = err.response?.data as { errors?: Record<string, string>; message?: string } | undefined;
        const firstFieldError = data?.errors ? Object.values(data.errors)[0] : undefined;
        // Re-throw so the modal re-enables its sign button and shows the reason inline.
        throw new Error(firstFieldError || data?.message || t("signingPage.submitError"));
      }
      throw new Error(t("signingPage.submitError"));
    }
  };

  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  if (state === "signing_success") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <div className="text-5xl mb-4">✓</div>
          <h1 className="text-2xl font-bold mb-2">{t("signingPage.successTitle")}</h1>
          <p className="text-base-content/70">{t("signingPage.successMessage")}</p>
        </div>
      </div>
    );
  }

  if (state === "error_expired") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <div className="text-5xl mb-4">⏰</div>
          <h1 className="text-2xl font-bold mb-2">{t("signingPage.expiredTitle")}</h1>
          <p className="text-base-content/70">{t("signingPage.expiredMessage")}</p>
        </div>
      </div>
    );
  }

  if (state === "error_signed") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <div className="text-5xl mb-4">✓</div>
          <h1 className="text-2xl font-bold mb-2">{t("signingPage.alreadySignedTitle")}</h1>
          <p className="text-base-content/70">{t("signingPage.alreadySignedMessage")}</p>
        </div>
      </div>
    );
  }

  if (state === "error_invalid" || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <div className="text-5xl mb-4">⚠</div>
          <h1 className="text-2xl font-bold mb-2">{t("signingPage.invalidTitle")}</h1>
          <p className="text-base-content/70">{t("signingPage.invalidMessage")}</p>
        </div>
      </div>
    );
  }

  return <CommoditySignTokenModal data={data} onSign={handleSign} />;
}
